import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import CategoryTabs from '@/components/feature/CategoryTabs';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug?: string }>();

  const currentCategory = slug ? categories.find((c) => c.slug === slug) : undefined;
  const filteredArticles = slug
    ? articles.filter((a) => a.category === slug)
    : articles;

  const breadcrumbItems = slug
    ? [
        { label: '首页', path: '/' },
        { label: '文章分类', path: '/category' },
        { label: currentCategory?.name || slug },
      ]
    : [
        { label: '首页', path: '/' },
        { label: '文章分类' },
      ];

  const pageTitle = slug
    ? `${currentCategory?.name || '分类'} - 膀胱健康管理`
    : '文章分类 - 膀胱健康管理';

  const pageDescription = slug && currentCategory
    ? currentCategory.description
    : '浏览膀胱健康管理全部文章分类，涵盖憋尿控制训练方法、膀胱控制能力提升、尿频改善方案等六大知识领域。';

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDescription,
    url: `https://bladderhealth.cn/category${slug ? `/${slug}` : ''}`,
    inLanguage: 'zh-CN',
    about: {
      '@type': 'Thing',
      name: '膀胱健康与憋尿控制',
    },
  };

  return (
    <>
      <SchemaOrg data={collectionSchema} />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 md:pt-28 pb-12 md:pb-16">
          <div className="w-full px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            {/* Page Header */}
            <div className="mb-8 md:mb-10">
              {slug && currentCategory ? (
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                      <i className={`${currentCategory.icon} text-lg text-primary-600`}></i>
                    </div>
                    <h1 className="font-heading text-xl md:text-3xl text-foreground-950">
                      {currentCategory.name}
                    </h1>
                  </div>
                  <p className="text-sm text-foreground-600 max-w-2xl leading-relaxed">
                    {currentCategory.description}
                  </p>
                </div>
              ) : (
                <div>
                  <h1 className="font-heading text-xl md:text-3xl text-foreground-950">
                    文章分类
                  </h1>
                  <p className="mt-2 text-sm text-foreground-600 max-w-2xl leading-relaxed">
                    浏览全部知识分类，找到您需要的膀胱健康管理内容。涵盖憋尿控制训练方法、膀胱控制能力提升、尿频改善方案等六大专业领域。
                  </p>
                </div>
              )}
            </div>

            {/* Category Tabs */}
            <div className="mb-8">
              <CategoryTabs categories={categories} />
            </div>

            {/* Category Overview (when no slug) */}
            {!slug && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mb-10">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group bg-background-50 border border-background-200/60 rounded-lg p-5 md:p-6 hover:border-primary-200 transition-all duration-300 cursor-pointer flex items-start gap-4"
                  >
                    <div className="w-11 h-11 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 transition-colors">
                      <i className={`${cat.icon} text-xl text-primary-600`}></i>
                    </div>
                    <div>
                      <h2 className="font-heading text-base text-foreground-950 group-hover:text-primary-600 transition-colors">
                        {cat.name}
                      </h2>
                      <p className="mt-1 text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                        {cat.description}
                      </p>
                      <span className="inline-block mt-2 text-xs text-primary-600 font-medium">
                        {cat.articleCount} 篇文章
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Article List */}
            {slug && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {filteredArticles.length > 0 ? (
                  filteredArticles.map((article) => {
                    const cat = categories.find((c) => c.slug === article.category);
                    return (
                      <ArticleCard
                        key={article.slug}
                        article={article}
                        categoryName={cat?.name || article.category}
                      />
                    );
                  })
                ) : (
                  <div className="col-span-full text-center py-16">
                    <div className="w-16 h-16 mx-auto bg-background-100 rounded-full flex items-center justify-center mb-4">
                      <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                    </div>
                    <p className="text-foreground-500">该分类下暂无文章</p>
                  </div>
                )}
              </div>
            )}

            {/* All articles list when no slug selected */}
            {!slug && (
              <div>
                <h2 className="font-heading text-lg md:text-xl text-foreground-950 mb-5">
                  全部文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {filteredArticles.map((article) => {
                    const cat = categories.find((c) => c.slug === article.category);
                    return (
                      <ArticleCard
                        key={article.slug}
                        article={article}
                        categoryName={cat?.name || article.category}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}